import { useMemo } from 'react'
import { OpportunitySummary } from '../components/OpportunitySummary'
import { OpportunityDescription } from '../components/OpportunityDescription'
import { formatRelative } from '../lib/relativeTime'
import { applicationsOpen, applyBy } from '../lib/applicationsOpen'
import { TalentGettingStarted } from '../components/GettingStartedCard'
import { useNavigate } from 'react-router-dom'
import { focusAreaWithName } from '../types/focus_area'
import { ErrorCard } from '../components/ErrorCard'
import {
  makeStyles,
  mergeClasses,
  tokens,
  Badge,
  Body1,
  Button,
  Caption1,
  Link,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle1,
  Subtitle2,
  Text,
  Title2,
} from '@fluentui/react-components'
import {
  Alert24Regular,
  ArrowRight16Regular,
  Briefcase24Regular,
  Sparkle24Regular,
} from '@fluentui/react-icons'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { useMyApplications, useWithdrawApplication } from '../hooks/useApplications'
import { useMyInvitations, useRespondToInvitation } from '../hooks/useInvitations'
import { useToast } from '../components/ToastProvider'
import { apiErrorMessage } from '../lib/apiError'
import { isInResourcingBand } from '../lib/labels'
import { deriveStaffing } from '../lib/staffing'
import { useDemands } from '../hooks/useDemands'
import { useMySkills } from '../hooks/useUserSkills'
import { useCapabilities } from '../hooks/useCapabilities'
import { coveredSkills, skillIndex } from '../lib/skillMatch'
import type { Application, ApplicationStatus } from '../types/application'
import type { Demand } from '../types/demand'

/**
 * Talent Dashboard (mockup §5142-5300).
 *
 * Mirrors `renderTalent` / `renderInvitationCard` / `renderApplicationCard` /
 * `renderOpportunityCard` from the mockup, ported onto the v2 substrate.
 *
 * Data sources:
 *  - Greeting:        `useAuth().user.name` (first token used as first name).
 *  - Invitations:     no backend hook exists yet; the section is feature-flagged
 *                     by an `invitations` array (currently empty) so the markup
 *                     is wired but the section stays hidden — matches the
 *                     mockup's "only renders if any" gating.
 *  - Applications:    `useMyApplications` (existing canonical hook).
 *  - Opportunities:   local `useRecommendedDemands` thin wrapper around the
 *                     canonical `useDemands({ stage: 'open_to_application' })`,
 *                     ranked by skill overlap with `useMySkills`.
 *  - Profile meter:   measured from the effective user's own fields plus
 *                     `useMySkills`. It was a hardcoded 65% until #1252.
 */

// ────────────────────────────────────────────────────────────────
// Local types — invitation shape mirrors mockup field-for-field.
// Backend hook does not exist yet; defined here for forward
// compatibility so the card markup can stay accurate.
// ────────────────────────────────────────────────────────────────

interface TalentInvitation {
  id: number | string
  demandId: number
  demandTitle: string
  demandCode?: string | null
  invitedBy: string
  invitedByRole: string
  daysAgo: number
  deadlineDaysLeft?: number | null
  quote: string
  /**
   * The demand's stage. The API has always sent it and this view-model used to
   * drop it here, which is why the card offered an Accept button on a demand
   * that could no longer be accepted.
   */
  demandStage?: string | null
}

interface RecommendedOpportunity extends Demand {
  matchSkills: number
  totalSkills: number
  matchedNames: string[]
  daysLeft: number | null
}

// ────────────────────────────────────────────────────────────────
// Hook: useRecommendedDemands — ranks open demands by skill overlap,
// leaving out the ones there is nothing left to do about.
// Kept colocated since no recommended-demands hook exists yet.
// ────────────────────────────────────────────────────────────────

function daysUntil(iso?: string | null): number | null {
  if (!iso) return null
  const close = new Date(iso).getTime()
  if (Number.isNaN(close)) return null
  return Math.max(0, Math.ceil((close - Date.now()) / 86_400_000))
}

export function useRecommendedDemands(limit = 2) {
  const demandsQuery = useDemands({ stage: 'resourcing', page_size: 200, allPages: true })
  const skillsQuery = useMySkills()
  // The page reads both as well; React Query dedupes the keys.
  const appsQuery = useMyApplications()
  const invitationsQuery = useMyInvitations()
  // Which category each skill sits in, so a whole category counts
  // (lib/skillMatch). Not waited on: without it, terms match by name alone.
  const capabilitiesQuery = useCapabilities()
  // Wait for both, or an invited demand shows here and then jumps up to its
  // own section. A failed one is not waited on: the page reports it already.
  const engagementPending = appsQuery.isPending || invitationsQuery.isPending

  const items: RecommendedOpportunity[] = useMemo(() => {
    if (engagementPending) return []
    // Applied in any state, or invited: pending invitations have their own
    // section above, and the rest were already answered.
    const engaged = new Set([
      ...(appsQuery.data?.items ?? []).map((a) => a.demand_id),
      ...(invitationsQuery.data ?? []).map((i) => i.demand_id),
    ])
    const demands = (demandsQuery.data?.items ?? []).filter(
      (d) =>
        !engaged.has(d.id) &&
        // Not recruiting = fully staffed. Server summary first (lib/staffing.ts).
        deriveStaffing(d).recruiting &&
        // Closed by council or the caretaker. A passed deadline is not a close
        // (models/demand.py `sql_posted`, 2026-10-16).
        applicationsOpen(d),
    )
    const mySkillNames = (skillsQuery.data ?? []).map((s) => s.capability_name)
    const index = skillIndex(capabilitiesQuery.data)
    const scored = demands.map<RecommendedOpportunity>((d) => {
      const matched = coveredSkills(index, mySkillNames, d.skills ?? [])
      return {
        ...d,
        matchSkills: matched.length,
        totalSkills: (d.skills ?? []).length,
        matchedNames: matched,
        daysLeft: daysUntil(d.application_closes_at),
      }
    })
    scored.sort((a, b) => {
      if (b.matchSkills !== a.matchSkills) return b.matchSkills - a.matchSkills
      return (a.daysLeft ?? Infinity) - (b.daysLeft ?? Infinity)
    })
    return scored.slice(0, limit)
  }, [
    demandsQuery.data,
    skillsQuery.data,
    capabilitiesQuery.data,
    appsQuery.data,
    invitationsQuery.data,
    engagementPending,
    limit,
  ])

  return {
    items,
    isLoading: demandsQuery.isLoading || skillsQuery.isLoading || engagementPending,
    isError: demandsQuery.isError || skillsQuery.isError,
    refetch: () => {
      void demandsQuery.refetch()
      void skillsQuery.refetch()
    },
  }
}

// ────────────────────────────────────────────────────────────────
// Styles — Fluent v9 makeStyles, tokens-only.
// ────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXL,
    maxWidth: '1100px',
  },
  greetingBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  greetingSub: {
    color: tokens.colorNeutralForeground3,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionHead: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  sectionIcon: {
    color: tokens.colorBrandForeground1,
    display: 'inline-flex',
    alignItems: 'center',
  },
  sectionTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  sectionCount: {
    marginLeft: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
  },
  sectionCta: {
    marginLeft: 'auto',
  },
  // Invitation card
  invitationCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `2px solid ${tokens.colorBrandStroke1}`,
    boxShadow: tokens.shadow4,
  },
  invitationBody: {
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  invitationTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  invitationMeta: {
    color: tokens.colorNeutralForeground3,
  },
  invitationMetaStrong: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  deadlineWarning: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  invitationQuote: {
    marginTop: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalM,
    borderLeft: `3px solid ${tokens.colorBrandStroke2}`,
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground2,
  },
  invitationActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    flexShrink: 0,
  },
  // Application row
  appList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  appRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingHorizontalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  dotPending: {
    backgroundColor: tokens.colorPaletteYellowBackground3,
  },
  dotApproved: {
    backgroundColor: tokens.colorPaletteGreenBackground3,
  },
  dotRejected: {
    backgroundColor: tokens.colorPaletteRedBackground3,
  },
  dotWithdrawn: {
    backgroundColor: tokens.colorNeutralForeground4,
  },
  appInfo: {
    flex: '1 1 auto',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  appTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  appTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  appMeta: {
    color: tokens.colorNeutralForeground3,
  },
  appFeedback: {
    marginTop: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalS,
    fontStyle: 'italic',
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
  },
  appActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  // Opportunity grid
  oppGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  oppCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow2,
  },
  oppTitle: {
    margin: 0,
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  matchLine: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  skillsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalXS,
    marginBottom: tokens.spacingVerticalS,
  },
  oppFoot: {
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  // Profile meter
  profileMeter: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalL,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  profileMeterTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
  },
  profileMeterTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  profileMeterDesc: {
    color: tokens.colorNeutralForeground3,
  },
  meterBar: {
    width: '100%',
    height: '8px',
    backgroundColor: tokens.colorNeutralBackground4,
    borderRadius: tokens.borderRadiusCircular,
    overflow: 'hidden',
  },
  meterFill: {
    height: '100%',
    backgroundColor: tokens.colorBrandBackground,
    transitionProperty: 'width',
    transitionDuration: '200ms',
  },
  empty: {
    color: tokens.colorNeutralForeground3,
  },
})

// ────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────

/** "a, b and c" — the caption reads as a sentence, so it needs the conjunction. */
export function listPhrase(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? ''
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`
}

const STATUS_LABELS: Record<ApplicationStatus, string> = {
  pending: 'Under review',
  approved: 'Approved',
  rejected: 'Not selected',
  withdrawn: 'Withdrawn',
}

const STATUS_BADGE_COLOR: Record<
  ApplicationStatus,
  'warning' | 'success' | 'danger' | 'informative'
> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  withdrawn: 'informative',
}

function firstName(name?: string | null): string {
  if (!name) return 'there'
  const first = name.trim().split(/\s+/)[0]
  return first || 'there'
}

function formatAppMeta(app: Application): string {
  const parts: string[] = []
  if (app.proposed_focus) parts.push(app.proposed_focus)
  if (app.proposed_hours_per_week) parts.push(`${app.proposed_hours_per_week}h/wk`)
  if (app.proposed_duration) parts.push(app.proposed_duration)
  const submitted = new Date(app.created_at)
  if (!Number.isNaN(submitted.getTime())) {
    parts.push(`submitted ${submitted.toLocaleDateString()}`)
  }
  return parts.join(' · ')
}

// ────────────────────────────────────────────────────────────────
// Main page component
// ────────────────────────────────────────────────────────────────

interface TalentDashboardProps {
  /** Optional mock invitations (used in tests). Defaults to an empty list. */
  invitations?: TalentInvitation[]
  onAcceptInvitation?: (id: TalentInvitation['id']) => void
  onDeclineInvitation?: (id: TalentInvitation['id']) => void
  onViewDemand?: (demandId: number) => void
  onViewApplication?: (id: number) => void
  onWithdrawApplication?: (id: number) => void
  onApplyOpportunity?: (demandId: number) => void
  onBrowseAll?: () => void
  onSeeAllApplications?: () => void
  onEditProfile?: () => void
}

export function TalentDashboard({
  invitations = [],
  onAcceptInvitation,
  onDeclineInvitation,
  onViewDemand,
  onViewApplication,
  onWithdrawApplication,
  onApplyOpportunity,
  onBrowseAll,
  onSeeAllApplications,
  onEditProfile,
}: TalentDashboardProps = {}) {
  const styles = useStyles()
  const user = useEffectiveUser()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const withdrawMutation = useWithdrawApplication()
  const respondInvitation = useRespondToInvitation()

  // Default navigation callbacks when none provided by parent
  const viewDemand = onViewDemand ?? ((id: number) => navigate(`/demand/${id}`))
  const viewApplication = onViewApplication ?? ((id: number) => navigate(`/demand/${id}`))
  const browseAll = onBrowseAll ?? (() => navigate('/opportunities'))
  const seeAllApps = onSeeAllApplications ?? (() => navigate('/opportunities'))
  const editProfile = onEditProfile ?? (() => navigate('/profile'))
  const applyOpp = onApplyOpportunity ?? ((id: number) => navigate(`/demand/${id}`))
  const withdrawApp =
    onWithdrawApplication ??
    ((id: number) => {
      // Native confirm — withdraw is destructive and was previously
      // a single-click footgun. See #754 / T4 walk.
      if (window.confirm('Withdraw this application? This cannot be undone.')) {
        withdrawMutation.mutate(id, {
          onError: (err) =>
            showToast({
              title: "Couldn't withdraw the application",
              body: apiErrorMessage(err),
              intent: 'error',
            }),
        })
      }
    })

  const appsQuery = useMyApplications()
  const invitationsQuery = useMyInvitations()
  // Already in flight for the recommendations below; React Query dedupes the
  // key, so reading it here costs no extra request.
  const mySkills = useMySkills()
  const recommended = useRecommendedDemands(2)

  // Map real invitations to the TalentInvitation shape expected by cards
  const realInvitations: TalentInvitation[] = useMemo(() => {
    const raw = invitationsQuery.data ?? []
    return raw
      .filter((inv) => inv.status === 'pending')
      .map((inv) => ({
        id: inv.id,
        demandId: inv.demand_id,
        demandTitle: inv.demand_title ?? `Demand #${inv.demand_id}`,
        demandCode: inv.demand_code ?? null,
        invitedBy: inv.inviter_name ?? 'Council',
        invitedByRole: 'Council',
        daysAgo: Math.max(0, Math.floor((Date.now() - new Date(inv.created_at).getTime()) / 86_400_000)),
        deadlineDaysLeft: null,
        quote: inv.message ?? '',
        demandStage: inv.demand_stage ?? null,
      }))
  }, [invitationsQuery.data])

  // Merge prop-injected mock invitations with real ones (tests use prop, prod uses real)
  const allInvitations = invitations.length > 0 ? invitations : realInvitations

  // The hook's own onError only invalidates the invitation list — it shows the
  // user nothing (see hooks/useInvitations.ts). Without a handler here, an
  // invitation that fails to accept looked exactly like one that succeeded and
  // then vanished from the list, so people clicked again.
  const respondFailed = (action: 'accept' | 'decline') => (err: unknown) =>
    showToast({
      title: action === 'accept' ? "Couldn't accept the invitation" : "Couldn't decline it",
      body: apiErrorMessage(err),
      intent: 'error',
    })

  const handleAcceptInvitation = onAcceptInvitation ?? ((id: TalentInvitation['id']) => {
    respondInvitation.mutate(
      { invitationId: Number(id), action: 'accept' },
      { onError: respondFailed('accept') },
    )
  })
  const handleDeclineInvitation = onDeclineInvitation ?? ((id: TalentInvitation['id']) => {
    respondInvitation.mutate(
      { invitationId: Number(id), action: 'decline' },
      { onError: respondFailed('decline') },
    )
  })

  const applications = appsQuery.data?.items ?? []

  // Tally every status, not two of them. The caption used to read
  // "0 approved · 0 pending" above a list of three rejected applications,
  // because the list renders everything and the count only knew about two
  // states. Deriving both from the same array is what keeps them agreeing.
  const statusTally = (Object.keys(STATUS_LABELS) as ApplicationStatus[])
    .map((status) => ({ status, count: applications.filter((a) => a.status === status).length }))
    .filter((t) => t.count > 0)

  if (appsQuery.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner label="Loading your dashboard..." />
      </div>
    )
  }

  if (appsQuery.isError) {
    return (
      <div className={styles.page}>
        <MessageBar intent="error">
          <MessageBarBody>
            Failed to load your dashboard. {apiErrorMessage(appsQuery.error)}
          </MessageBarBody>
        </MessageBar>
      </div>
    )
  }

  // Completion is measured, not asserted. `profileCompletion` was a default
  // prop of 65 that no caller ever passed, so a full profile and an empty one
  // read identically — and the caption told everyone to add "your publications
  // link", a field the profile page does not have. These four are what the
  // profile page actually edits and what a council searches on.
  const profileParts: { label: string; done: boolean }[] = [
    { label: 'a short bio', done: Boolean(user?.bio?.trim()) },
    { label: 'your skills', done: (mySkills.data?.length ?? 0) > 0 },
    { label: 'your focus areas', done: (user?.preferred_focus_areas?.length ?? 0) > 0 },
    { label: 'your GitHub handle', done: Boolean(user?.github_handle?.trim()) },
  ]
  const missingProfileParts = profileParts.filter((p) => !p.done).map((p) => p.label)
  // Until the skills query settles, "no skills" and "not asked yet" are the same
  // value, and a failed fetch never settles into a real one. Reporting 50% off a
  // question that was never answered is the same fault this whole change is
  // about, so the meter says it is still looking rather than guessing.
  const skillsUnknown = mySkills.isPending || mySkills.isError
  const pct = Math.round(
    ((profileParts.length - missingProfileParts.length) / profileParts.length) * 100,
  )

  return (
    <div className={styles.page}>
      <header className={styles.greetingBlock}>
        <Title2 as="h1">Hi {firstName(user?.name)},</Title2>
        <Caption1 className={styles.greetingSub}>
          Here&apos;s what&apos;s happening with your work today.
        </Caption1>
      </header>

      <TalentGettingStarted />

      {/* An invitation is a pre-approved seat, so silently showing none of them
          is the most consequential empty state on this page. The page already
          had an error branch — it covered the applications query and not this
          one, so a failed invitations fetch rendered as a healthy dashboard
          with nothing to respond to. Inline rather than whole-page: the
          applications below may have loaded perfectly well. */}
      {invitationsQuery.isError && (
        <MessageBar intent="warning" data-testid="invitations-error">
          <MessageBarBody>
            Couldn&apos;t check whether you have invitations.{' '}
            <Link onClick={() => void invitationsQuery.refetch()}>Try again</Link>
          </MessageBarBody>
        </MessageBar>
      )}

      {allInvitations.length > 0 && (
        <section
          className={styles.section}
          aria-labelledby="invitations-heading"
          data-testid="invitations-section"
        >
          <header className={styles.sectionHead}>
            <span className={styles.sectionIcon} aria-hidden>
              <Alert24Regular />
            </span>
            <Subtitle1
              as="h2"
              id="invitations-heading"
              className={styles.sectionTitle}
            >
              Needs your response
            </Subtitle1>
            <Caption1 className={styles.sectionCount}>
              {allInvitations.length} invitation{allInvitations.length !== 1 ? 's' : ''}
            </Caption1>
          </header>
          {allInvitations.map((inv) => (
            <InvitationCard
              key={inv.id}
              invitation={inv}
              onAccept={handleAcceptInvitation}
              onDecline={handleDeclineInvitation}
              onView={viewDemand}
            />
          ))}
        </section>
      )}

      <section
        className={styles.section}
        aria-labelledby="applications-heading"
        data-testid="applications-section"
      >
        <header className={styles.sectionHead}>
          <span className={styles.sectionIcon} aria-hidden>
            <Briefcase24Regular />
          </span>
          <Subtitle1
            as="h2"
            id="applications-heading"
            className={styles.sectionTitle}
          >
            Your applications
          </Subtitle1>
          <Caption1 className={styles.sectionCount} data-testid="applications-count">
            {statusTally.map((t) => `${t.count} ${STATUS_LABELS[t.status].toLowerCase()}`).join(' · ')}
          </Caption1>
          {seeAllApps && (
            <Button
              className={styles.sectionCta}
              appearance="subtle"
              size="small"
              icon={<ArrowRight16Regular />}
              iconPosition="after"
              onClick={seeAllApps}
            >
              See all
            </Button>
          )}
        </header>
        {applications.length > 0 ? (
          <div className={styles.appList}>
            {applications.map((app) => (
              <ApplicationCard
                key={app.id}
                application={app}
                onView={viewApplication}
                onWithdraw={withdrawApp}
              />
            ))}
          </div>
        ) : (
          <Caption1 className={styles.empty}>No applications yet.</Caption1>
        )}
      </section>

      {recommended.isError ? (
        <section aria-label="Recommendations">
          <ErrorCard title="Could not load recommendations" message="Please try again." retry={recommended.refetch} />
        </section>
      ) : recommended.items.length > 0 && (
        <section
          className={styles.section}
          aria-labelledby="opportunities-heading"
          data-testid="opportunities-section"
        >
          <header className={styles.sectionHead}>
            <span className={styles.sectionIcon} aria-hidden>
              <Sparkle24Regular />
            </span>
            <Subtitle1
              as="h2"
              id="opportunities-heading"
              className={styles.sectionTitle}
            >
              A few opportunities to consider
            </Subtitle1>
            {browseAll && (
              <Button
                className={styles.sectionCta}
                appearance="subtle"
                size="small"
                icon={<ArrowRight16Regular />}
                iconPosition="after"
                onClick={browseAll}
              >
                Browse all
              </Button>
            )}
          </header>
          <div className={styles.oppGrid} data-testid="opp-grid">
            {recommended.items.map((opp) => (
              <OpportunityCard
                key={opp.id}
                opportunity={opp}
                onApply={applyOpp}
              />
            ))}
          </div>
        </section>
      )}

      <aside
        className={styles.profileMeter}
        aria-label="Profile completeness"
        data-testid="profile-meter"
      >
        <div className={styles.profileMeterTopRow}>
          <div>
            <Text className={styles.profileMeterTitle} block>
              {skillsUnknown ? 'Checking your profile…' : `Your profile is ${pct}% complete`}
            </Text>
            <Caption1 className={styles.profileMeterDesc}>
              {skillsUnknown
                ? ' '
                : missingProfileParts.length > 0
                  ? `Add ${listPhrase(missingProfileParts)} to be discovered for more opportunities.`
                  : 'Everything councils search on is filled in.'}
            </Caption1>
          </div>
          <Button
            appearance="secondary"
            icon={<ArrowRight16Regular />}
            iconPosition="after"
            onClick={editProfile}
          >
            Edit profile
          </Button>
        </div>
        <div
          className={styles.meterBar}
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={skillsUnknown ? undefined : pct}
        >
          <div className={styles.meterFill} style={{ width: skillsUnknown ? '0%' : `${pct}%` }} />
        </div>
      </aside>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// Invitation card
// ────────────────────────────────────────────────────────────────

function InvitationCard({
  invitation,
  onAccept,
  onDecline,
  onView,
}: {
  invitation: TalentInvitation
  onAccept?: (id: TalentInvitation['id']) => void
  onDecline?: (id: TalentInvitation['id']) => void
  onView?: (demandId: number) => void
}) {
  const styles = useStyles()
  // Accepting is gated on the resourcing band server-side. This card used to
  // offer Accept regardless, so an invitation to a demand that had been parked,
  // closed or sent back to scoping sat here with a button that always 400'd.
  //
  // Absent stage is treated as acceptable rather than blocked: the mock
  // invitations the tests inject carry no stage, and a missing field must not
  // silently disable a live button. Being wrong that way costs a server-side
  // error the user can read; the other way hides a working action.
  const canAccept =
    invitation.demandStage == null || isInResourcingBand(invitation.demandStage)
  return (
    <article
      className={styles.invitationCard}
      data-invitation-id={invitation.id}
      data-testid={`invitation-card-${invitation.id}`}
    >
      <div className={styles.invitationBody}>
        <Subtitle2 as="h3" className={styles.invitationTitle}>
          {invitation.demandCode && (
            <Badge appearance="tint" size="small" style={{ marginRight: 6 }}>
              {invitation.demandCode}
            </Badge>
          )}
          {invitation.demandTitle}
        </Subtitle2>
        <Caption1 className={styles.invitationMeta}>
          Invited by{' '}
          <span className={styles.invitationMetaStrong}>
            {invitation.invitedBy}
          </span>{' '}
          ({invitation.invitedByRole}) · {invitation.daysAgo} days ago
        </Caption1>
        {invitation.deadlineDaysLeft != null && invitation.deadlineDaysLeft > 0 && (
          <Caption1 className={styles.deadlineWarning}>
            Application window closes in {invitation.deadlineDaysLeft} days
          </Caption1>
        )}
        {!canAccept && (
          <Caption1 style={{ color: tokens.colorPaletteYellowForeground2, fontStyle: 'italic' }}>
            This demand has moved on and is not resourcing right now — you can no
            longer accept. Decline to clear it, or leave it in case it comes back.
          </Caption1>
        )}
        {invitation.quote && (
          <Body1 className={styles.invitationQuote}>
            &ldquo;{invitation.quote}&rdquo;
          </Body1>
        )}
      </div>
      <div className={styles.invitationActions}>
        {canAccept ? (
          <Button
            appearance="primary"
            size="small"
            onClick={() => onAccept?.(invitation.id)}
            aria-label={`Accept invitation: ${invitation.demandTitle}`}
          >
            Accept
          </Button>
        ) : (
          <Button
            appearance="primary"
            size="small"
            disabled
            data-testid={`invitation-accept-blocked-${invitation.id}`}
            aria-label={`Cannot accept invitation: ${invitation.demandTitle} — the demand is no longer resourcing`}
          >
            Accept
          </Button>
        )}
        <Button
          appearance="secondary"
          size="small"
          onClick={() => onDecline?.(invitation.id)}
          aria-label={`Decline invitation: ${invitation.demandTitle}`}
        >
          Decline
        </Button>
        <Button
          appearance="subtle"
          size="small"
          onClick={() => onView?.(invitation.demandId)}
          aria-label={`View demand: ${invitation.demandTitle}`}
        >
          View
        </Button>
      </div>
    </article>
  )
}

// ────────────────────────────────────────────────────────────────
// Application card
// ────────────────────────────────────────────────────────────────

function ApplicationCard({
  application,
  onView,
  onWithdraw,
}: {
  application: Application
  onView?: (id: number) => void
  onWithdraw?: (id: number) => void
}) {
  const styles = useStyles()
  const status = application.status
  const dotClass =
    status === 'approved'
      ? styles.dotApproved
      : status === 'rejected'
        ? styles.dotRejected
        : status === 'withdrawn'
          ? styles.dotWithdrawn
          : styles.dotPending
  const canWithdraw = status === 'pending'
  const title = application.demand_title ?? `Demand #${application.demand_id}`
  const fa = application.focus_area_code
  // The demand was withdrawn by council. Until this existed the row rendered as
  // an ordinary live application — `status` is still 'pending' and
  // `demand_stage` still reports whatever stage it was deleted from — while
  // View led to a 404. Silence dressed up as normal.
  const isDeleted = !!application.demand_deleted
  // The demand left the resourcing band, so nobody is reviewing this right now.
  // Frozen means "might come back"; deleted never does, so deleted wins the caption.
  //
  // Uses the shared band rather than `!== 'resourcing'`, which was wrong for the
  // two legacy stages that alias to it — a row still carrying `staffed` or
  // `open_to_application` would have been reported frozen while the server was
  // perfectly happy to act on it.
  const isFrozen =
    !isDeleted &&
    status === 'pending' &&
    !!application.demand_stage &&
    !isInResourcingBand(application.demand_stage)

  return (
    <article
      className={styles.appRow}
      data-status={status}
      data-app-id={application.id}
      data-testid={`application-card-${application.id}`}
    >
      <span
        className={mergeClasses(styles.statusDot, dotClass)}
        aria-hidden
      />
      <div className={styles.appInfo}>
        <div className={styles.appTitleRow}>
          {application.demand_code && (
            <Badge appearance="tint" size="small">
              {application.demand_code}
            </Badge>
          )}
          <Text className={styles.appTitle}>{title}</Text>
          <Badge appearance="filled" color={STATUS_BADGE_COLOR[status]} size="small">
            {STATUS_LABELS[status]}
          </Badge>
          {fa && (
            <Badge appearance="outline" size="small">
              {focusAreaWithName(fa)}
            </Badge>
          )}
        </div>
        {isDeleted && (
          <Caption1 style={{ color: tokens.colorPaletteRedForeground1, fontStyle: 'italic' }}>
            This demand was withdrawn — your application will not be reviewed
          </Caption1>
        )}
        {isFrozen && (
          <Caption1 style={{ color: tokens.colorPaletteYellowForeground2, fontStyle: 'italic' }}>
            This demand has moved out of resourcing — nobody is reviewing your
            application right now
          </Caption1>
        )}
        <Caption1 className={styles.appMeta}>{formatAppMeta(application)}</Caption1>
        <Caption1 className={styles.appMeta}>
          Last updated{' '}
          <span title={new Date(application.updated_at).toLocaleString()}>
            {formatRelative(application.updated_at)}
          </span>
        </Caption1>
        {!isDeleted && !isFrozen && (
          <Caption1 className={styles.appMeta}>
            {status === 'pending'
              ? 'Next: council reviews your application. No response date has been set; your decision and feedback will appear here.'
              : status === 'approved'
                ? 'Next: coordinate the start and commitment with the demand lead.'
                : status === 'rejected'
                  ? 'Next: review any feedback below and explore other opportunities.'
                  : 'Your application was withdrawn. Browse opportunities when you are ready.'}
          </Caption1>
        )}
        {application.feedback && (
          <Body1 className={styles.appFeedback}>{application.feedback}</Body1>
        )}
      </div>
      <div className={styles.appActions}>
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowRight16Regular />}
          iconPosition="after"
          // The detail page 404s for a talent the moment the demand is deleted,
          // so the button would be a trapdoor. The row stays — they applied,
          // they are entitled to keep seeing that — only the dead end goes.
          disabled={isDeleted}
          onClick={() => onView?.(application.demand_id)}
          aria-label={`View demand: ${title}`}
        >
          View
        </Button>
        {/* Withdraw stays available on a frozen or withdrawn demand — this is
            the only withdraw affordance in the app, and hiding it stranded the
            application permanently: the backend's only guard is
            `status != PENDING`, so it would have accepted this at any time, and
            a later re-post then refused a fresh application with "Withdraw it
            first", an instruction the UI made impossible to follow.
            A deleted demand is different: the row is a tombstone and the
            backend 404s it. */}
        {canWithdraw && !isDeleted && (
          <Button
            appearance="subtle"
            size="small"
            onClick={() => onWithdraw?.(application.id)}
            aria-label={`Withdraw application: ${title}`}
          >
            Withdraw
          </Button>
        )}
      </div>
    </article>
  )
}

// ────────────────────────────────────────────────────────────────
// Opportunity card (compact dashboard variant)
// ────────────────────────────────────────────────────────────────

function OpportunityCard({
  opportunity,
  onApply,
}: {
  opportunity: RecommendedOpportunity
  onApply?: (demandId: number) => void
}) {
  const styles = useStyles()
  const ahead = applyBy(opportunity.application_closes_at)
  return (
    <article
      className={styles.oppCard}
      data-opp-id={opportunity.id}
      data-testid={`opp-card-${opportunity.id}`}
      aria-label={opportunity.title}
    >
      <Subtitle2 as="h3" className={styles.oppTitle}>
        {opportunity.title}
      </Subtitle2>
      <OpportunityDescription text={opportunity.description} />
      <OpportunitySummary demand={opportunity} matchedSkills={opportunity.matchedNames} />
      <div className={styles.oppFoot}>
        <Caption1>
          {focusAreaWithName(opportunity.focus_area_code)}
          {ahead ? ` · ${ahead}` : ''}
        </Caption1>
        <Button
          appearance="primary"
          size="small"
          icon={<ArrowRight16Regular />}
          iconPosition="after"
          onClick={() => onApply?.(opportunity.id)}
          aria-label={`Apply to ${opportunity.title}`}
        >
          Apply
        </Button>
      </div>
    </article>
  )
}

export default TalentDashboard
